//Player history
//This will go through all the leagues/teams that a player used to play for
//and show all their stats.
import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import './Players.css'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import YouTube from 'react-youtube'
import Footer from '../../shared/components/UIElements/Footer'
import PlayerHistoryHome from '../../shared/components/Navigation/PlayerHistoryHome'

var getYouTubeID = require('get-youtube-id')

const PlayerHistory = () => {
	console.log('inside PlayerHistory')

	const { playerId } = useParams()

	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)

	// player info
	const [firstName, setFirstName] = useState('')
	const [lastName, setLastName] = useState('')
	const [middleInitial, setMiddleInitial] = useState('')
	const [photo, setPhoto] = useState()

	// history rosters (ALWAYS an array)
	const [loadedRosters, setLoadedRosters] = useState([])
	const [goalieLoading, setGoalieLoading] = useState(false)
	const [goalieMixLoading, setGoalieMixLoading] = useState(false)

	// videos
	const [listOfVideos, setListOfVideos] = useState([])
	const [ids, setIds] = useState([])
	const [videoTitles, setVideoTitles] = useState([])
	const [videoCaptions, setVideoCaptions] = useState([])

	// goalie logic flags
	const [processDone, setProcessDone] = useState(false)
	const [onlyBeenGoalie, setOnlyBeenGoalie] = useState(false)
	const [hasPlayedOutAndBeenGoalie, setHasPlayedOutAndBeenGoalie] =
		useState(false)
	const [neverPlayedGoalie, setNeverPlayedGoalie] = useState(false)

	// goalie stats data
	const [goalieStats, setGoalieStats] = useState(false)
	const [goalieStatsByRosterSeason, setGoalieStatsByRosterSeason] = useState([])
	const [goalieAndNonGoalieStats, setGoalieAndNonGoalieStats] = useState(false)
	const [seasonsWherePlayerWasGoalie, setSeasonsWherePlayerWasGoalie] =
		useState()
	const [seasonsWherePlayerWasNotGoalie, setSeasonsWherePlayerWasNotGoalie] =
		useState()
	const [isMobile440, setIsMobile440] = useState(false)

	// Make a call to getPlayerHistoryByPlayerId in player-controller
	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player/${playerId}/history`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				console.log('responseData: ' + JSON.stringify(responseData))

				setLoadedRosters(responseData.listOfRosters || []) // <-- ALWAYS array
				setFirstName(responseData.firstName || '')
				setLastName(responseData.lastName || '')
				setMiddleInitial(responseData.middleInitial || '')
				setPhoto(responseData.photo)
				setListOfVideos(responseData.listOfVideos || [])
			} catch (err) {
				setError(err.message)
			}
			setIsLoading(false)
		}
		sendRequest()
	}, [playerId])

	// Videos -> derive ids/titles/captions
	useEffect(() => {
		if (!listOfVideos || listOfVideos.length === 0) {
			setIds([])
			setVideoTitles([])
			setVideoCaptions([])
			return
		}

		setVideoTitles(listOfVideos.map((v) => v.videoTitle))
		setIds(listOfVideos.map((v) => getYouTubeID(v.videoURL)))
		setVideoCaptions(listOfVideos.map((v) => v.videoCaption))
	}, [listOfVideos])

	//
	//
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile440(window.matchMedia('(max-width: 440px)').matches)
		}

		checkMobile()

		window.addEventListener('resize', checkMobile)

		return () => {
			window.removeEventListener('resize', checkMobile)
		}
	}, [])

	const goalieStatsMap = React.useMemo(() => {
		// key: teamId|year (matches your roster rows)
		const map = new Map()
		;(goalieStatsByRosterSeason || []).forEach((s) => {
			if (!s) return
			map.set(`${s.teamId}|${s.year}`, s)
		})
		return map
	}, [goalieStatsByRosterSeason])

	// Determine goalie “mode” based on loaded rosters
	useEffect(() => {
		if (!loadedRosters) return

		let wasGoalie = 0
		let wasOut = 0

		loadedRosters.forEach((roster) => {
			if (roster.isGoalie) wasGoalie += 1
			else wasOut += 1
		})

		setOnlyBeenGoalie(wasGoalie > 0 && wasOut === 0)
		setHasPlayedOutAndBeenGoalie(wasGoalie > 0 && wasOut > 0)
		setNeverPlayedGoalie(wasGoalie === 0 && wasOut > 0)
		setProcessDone(true)
	}, [loadedRosters])

	useEffect(() => {
		if (!processDone) return

		// ONLY GOALIE
		if (!goalieStats && onlyBeenGoalie) {
			getGoalieStats(playerId)
			return
		}

		// GOALIE + SKATER
		if (!goalieAndNonGoalieStats && hasPlayedOutAndBeenGoalie) {
			getGoalieAndNonGoalieStats(playerId)
		}
	}, [
		processDone,
		onlyBeenGoalie,
		hasPlayedOutAndBeenGoalie,
		goalieStats,
		goalieAndNonGoalieStats,
		playerId,
	])

	// player-controller, getGoalieStats
	const getGoalieStats = async (playerId) => {
		setGoalieLoading(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/player/goalieStats/${playerId}`,
			)

			const responseData = await response.json()

			if (!response.ok) {
				throw new Error(responseData.message)
			}

			setGoalieStatsByRosterSeason(responseData.goalieStatsByRosterSeason || [])
		} catch (err) {
			setError(err.message)
		} finally {
			setGoalieLoading(false)
			setGoalieStats(true)
		}
	}

	const getGoalieAndNonGoalieStats = async (playerId) => {
		setGoalieMixLoading(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/player/goalieAndNonGoalieStats/${playerId}`,
			)
			const responseData = await response.json()
			if (!response.ok) throw new Error(responseData.message)

			setSeasonsWherePlayerWasGoalie(responseData.seasonsWherePlayerWasGoalie)
			setSeasonsWherePlayerWasNotGoalie(
				responseData.seasonsWherePlayerWasNotGoalie,
			)
			setGoalieAndNonGoalieStats(true)
		} catch (err) {
			setError(err.message)
		} finally {
			setGoalieMixLoading(false)
		}
	}

	useEffect(() => {
		setGoalieStats(false)
		setGoalieAndNonGoalieStats(false)

		setGoalieStatsByRosterSeason(null)
		setSeasonsWherePlayerWasGoalie(null)
		setSeasonsWherePlayerWasNotGoalie(null)

		setGoalieLoading(false)
		setGoalieMixLoading(false)
	}, [playerId])

	// IMPORTANT: trigger goalie fetches in an effect (NOT during render)
	useEffect(() => {
		if (!processDone) return
		if (!playerId) return
		if (!loadedRosters) return

		// If player has no history, don't fetch goalie stats.
		if (loadedRosters.length === 0) return

		if (!goalieStats && onlyBeenGoalie) {
			getGoalieStats(playerId)
			return
		}

		if (!goalieStats && !goalieAndNonGoalieStats && hasPlayedOutAndBeenGoalie) {
			getGoalieAndNonGoalieStats(playerId)
		}
	}, [
		processDone,
		playerId,
		loadedRosters,
		onlyBeenGoalie,
		hasPlayedOutAndBeenGoalie,
		goalieStats,
		goalieAndNonGoalieStats,
	])

	const errorHandler = () => {
		setError(null)
	}

	const hasRosters = Array.isArray(loadedRosters) && loadedRosters.length > 0

	let rem = '20rem'
	if (hasRosters) {
		rem = loadedRosters.length > 10 ? '2rem' : '20rem'
	}

	const renderTournamentCell = (year, leagueName) => {
		return (
			<div className='tournamentCell'>
				<div className='tournamentYear'>{year}</div>
				<div className='tournamentName'>{leagueName}</div>
			</div>
		)
	}

	return (
		<div style={{ overflowX: 'hidden' }}>
			<React.Fragment>
				<PlayerHistoryHome />
				<ErrorModal error={error} onClear={errorHandler} />

				{/* INITIAL LOAD (player + history fetch) */}
				{isLoading && (
					<div className='center' style={{ marginTop: '6rem' }}>
						<LoadingSpinner />
					</div>
				)}

				{/* PAGE CONTENT (once initial load finishes) */}
				{!isLoading && (
					<div className='Roster' style={{ marginTop: '1rem' }}>
						{photo && (
							<div className='photoDiv'>
								<img
									src={`${process.env.REACT_APP_AWS_BUCKET}/${photo}`}
									className='playerPhoto'
									alt='player'
								/>
							</div>
						)}

						<h1>
							{firstName} {middleInitial} {lastName}
						</h1>
						<h3 className='h3Center'>Player History</h3>
						<p style={{ textAlign: 'center', fontSize: 'xx-small' }}>
							(GWG: Game-Winning Goals)
						</p>

						{/* WAITING FOR GOALIE MODE DECISION */}
						{!processDone && (
							<div className='center' style={{ marginTop: '2rem' }}>
								<LoadingSpinner />
							</div>
						)}

						{/* NO HISTORY */}
						{processDone && !hasRosters && (
							<p style={{ textAlign: 'center' }}>
								No roster history found for this player.
							</p>
						)}

						{/* HAS HISTORY */}
						{processDone && hasRosters && (
							<React.Fragment>
								{/* ONLY GOALIE */}
								{onlyBeenGoalie && (
									<React.Fragment>
										{goalieLoading && (
											<div className='center' style={{ marginTop: '2rem' }}>
												<LoadingSpinner />
											</div>
										)}

										{!goalieLoading && goalieStatsByRosterSeason && (
											<table id='bottomRoster'>
												<thead>
													<tr>
														<th></th>
														<th>Team</th>
														{!isMobile440 && <th>Division</th>}
														<th>W</th>
														<th>L</th>
														<th>SO</th>
														<th>GAA</th>
													</tr>
												</thead>
												<tbody>
													{loadedRosters.map((team, key) => {
														const stats = goalieStatsMap.get(
															`${team.teamId}|${team.year}`,
														)

														return (
															<tr key={key}>
																<td>
																	{isMobile440 && team.leagueAbbreviation
																		? team.leagueAbbreviation
																		: team.leagueName}
																</td>
																{/* <td>
																	<Link
																		style={{
																			textDecoration: 'none',
																			fontWeight: 600,
																			color: 'black',
																		}}
																		to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
																	>
																		{isMobile440 && team.abbreviation
																			? team.abbreviation
																			: team.teamName}
																	</Link>
																</td> */}
																<td>
																	<Link
																		style={{
																			textDecoration: 'none',
																			fontWeight: 600,
																			color: 'black',
																		}}
																		to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
																	>
																		{isMobile440 && team.abbreviation
																			? team.abbreviation
																			: team.teamName}
																	</Link>

																	{isMobile440 && (
																		<div>{team.divisionName}</div>
																	)}
																</td>

																{!isMobile440 && <td>{team.divisionName}</td>}

																<td>{stats ? stats.wins : 0}</td>
																<td>{stats ? stats.losses : 0}</td>
																<td>{stats ? stats.shutouts : 0}</td>
																<td>{stats ? stats.gaa : 0}</td>
															</tr>
														)
													})}
												</tbody>
											</table>
										)}
									</React.Fragment>
								)}

								{/* NEVER GOALIE */}
								{neverPlayedGoalie && (
									<table id='roster'>
										<thead>
											<tr>
												<th></th>
												<th>Team</th>
												{!isMobile440 && <th>Division</th>}
												<th>G</th>
												<th>A</th>
												{/* <th>PG</th>
												<th>PA</th> */}
												<th>GWG</th>
												<th>Total</th>
												<th>Playoffs</th>
												<th>PIM</th>
											</tr>
										</thead>
										<tbody>
											{loadedRosters.map((team, key) => (
												<tr key={key}>
													<td>
														{isMobile440 && team.leagueAbbreviation
															? team.leagueAbbreviation
															: team.leagueName}
													</td>
													{/* <td>
														<Link
															style={{
																textDecoration: 'none',
																fontWeight: 600,
																color: 'black',
															}}
															to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
														>
															{isMobile440 && team.abbreviation
																? team.abbreviation
																: team.teamName}
														</Link>
													</td> */}
													<td>
														<Link
															style={{
																textDecoration: 'none',
																fontWeight: 600,
																color: 'black',
															}}
															to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
														>
															{isMobile440 && team.abbreviation
																? team.abbreviation
																: team.teamName}
														</Link>

														{isMobile440 && <div>{team.divisionName}</div>}
													</td>
													{!isMobile440 && <td>{team.divisionName}</td>}
													<td>{team.goals + team.playoffGoals}</td>
													<td>{team.assists + team.playoffAssists}</td>
													{/* <td>{team.playoffGoals}</td>
													<td>{team.playoffAssists}</td> */}
													<td>{team.gwg}</td>
													<td>
														{team.goals +
															team.assists +
															team.playoffGoals +
															team.playoffAssists}
													</td>
													<td>
														{team.playoffGoals}G, {team.playoffAssists}A
													</td>
													<td>{team.pim}</td>
												</tr>
											))}
										</tbody>
									</table>
								)}

								{/* BOTH GOALIE + PLAYER */}
								{hasPlayedOutAndBeenGoalie && (
									<React.Fragment>
										{/* SKATER TABLE (shows immediately) */}
										{seasonsWherePlayerWasNotGoalie && (
											<table id='roster'>
												<thead>
													<tr>
														<th></th>
														<th>Team</th>
														{!isMobile440 && <th>Division</th>}
														<th>G</th>
														<th>A</th>
														{/* <th>PG</th>
														<th>PA</th> */}
														<th>GWG</th>
														<th>Total</th>
														<th>Playoffs</th>
														<th>PIM</th>
													</tr>
												</thead>
												<tbody>
													{seasonsWherePlayerWasNotGoalie.map((team, key) => {
														if (team.isGoalie) return null
														return (
															<tr key={key}>
																<td>
																	{isMobile440 && team.leagueAbbreviation
																		? team.leagueAbbreviation
																		: team.leagueName}
																</td>
																{/* <td>
																	<Link
																		style={{
																			textDecoration: 'none',
																			fontWeight: 600,
																			color: 'black',
																		}}
																		to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
																	>
																		{isMobile440 && team.abbreviation
																			? team.abbreviation
																			: team.teamName}
																	</Link>
																</td> */}
																<td>
																	<Link
																		style={{
																			textDecoration: 'none',
																			fontWeight: 600,
																			color: 'black',
																		}}
																		to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
																	>
																		{isMobile440 && team.abbreviation
																			? team.abbreviation
																			: team.teamName}
																	</Link>

																	{isMobile440 && (
																		<div>{team.divisionName}</div>
																	)}
																</td>
																{!isMobile440 && <td>{team.divisionName}</td>}
																<td>{team.goals + team.playoffGoals}</td>
																<td>{team.assists + team.playoffAssist}</td>
																{/* <td>{team.playoffGoals}</td>
																<td>{team.playoffAssists}</td> */}
																<td>{team.gwg}</td>
																<td>
																	{team.goals +
																		team.assists +
																		team.playoffGoals +
																		team.playoffAssists}
																</td>
																<td>
																	{team.playoffGoals}G, {team.playoffAssists}A
																</td>
																<td>{team.pim}</td>
															</tr>
														)
													})}
												</tbody>
											</table>
										)}

										<div style={{ marginTop: '3rem' }} />

										<h3 className='h3Center'>Goalie Stats</h3>

										{/* GOALIE TABLE (spinner while loading) */}
										{goalieMixLoading && (
											<div className='center' style={{ marginTop: '1.5rem' }}>
												<LoadingSpinner />
											</div>
										)}

										{!goalieMixLoading &&
											Array.isArray(seasonsWherePlayerWasGoalie) && (
												<table id='roster'>
													<thead>
														<tr>
															<th>Tournament</th>
															<th>Team</th>
															{!isMobile440 && <th>Division</th>}
															<th>W</th>
															<th>L</th>
															<th>SO</th>
															<th>GAA</th>
														</tr>
													</thead>
													<tbody>
														{seasonsWherePlayerWasGoalie.map((season, key) => (
															<tr key={key}>
																<td>
																	{isMobile440 && season.leagueAbbreviation
																		? season.leagueAbbreviation
																		: season.leagueName}
																</td>
																<td>
																	<Link
																		style={{
																			textDecoration: 'none',
																			fontWeight: 600,
																			color: 'black',
																		}}
																		to={`/league/${season.leagueId}/${season.divisionName}/${season.year}/${season.teamName}`}
																	>
																		{isMobile440 && season.abbreviation
																			? season.abbreviation
																			: season.teamName}
																	</Link>
																</td>
																<td>{season.divisionName}</td>
																<td>{season.wins}</td>
																<td>{season.losses}</td>
																<td>{season.shutouts}</td>
																<td>{season.gaa}</td>
															</tr>
														))}
													</tbody>
												</table>
											)}
									</React.Fragment>
								)}

								{/* VIDEOS */}
								{listOfVideos && listOfVideos.length > 0 && (
									<div className='section pt-5 playerVideos'>
										<div style={{ overflowX: 'hidden' }}>
											<div className='row'>
												<div className='col text-center mb-0'>
													<h1 className='text-sh-dark display-2'>
														Check it out!
													</h1>
												</div>
											</div>
											<div className='row'>
												<div className='col-lg-3 col-sm-3'></div>
												<div className='col-lg-6 col-sm-6'>
													{listOfVideos.map((video, key) => (
														<div key={key}>
															<h1 style={{ color: 'red' }}>
																{videoTitles[key]}
															</h1>
															<p
																className='caption'
																style={{ textAlign: 'center', color: 'black' }}
															>
																{videoCaptions[key]}
															</p>
															<YouTube
																style={{ paddingBottom: '3rem' }}
																videoId={ids[key]}
															/>
														</div>
													))}
												</div>
												<div className='col-lg-3 col-sm-3'></div>
											</div>
										</div>
									</div>
								)}
							</React.Fragment>
						)}
					</div>
				)}

				{/* FOOTER */}
				{(!listOfVideos || listOfVideos.length === 0) && (
					<div style={{ marginTop: '19rem' }}>
						<Footer />
					</div>
				)}
				{listOfVideos && listOfVideos.length > 0 && (
					<div style={{ marginTop: '7rem' }}>
						<Footer />
					</div>
				)}
			</React.Fragment>
		</div>
	)
}

export default PlayerHistory
